import { FIRST_PAGE } from '@/data/hook/shared/usePagination';
import usePendencyAdvancedFilters, {
  PendencyAdvancedFilters,
} from '@/data/hook/shared/pendencies/usePendencyAdvancedFilters';
import { useDynamicParams } from '@/data/hook/useDynamicParams';
import {
  STUDENT_EXAM_ORDERING_FIELDS,
  StudentExamFilterParams,
  StudentExamOrdering,
} from '@/data/services/studentExamServices';
import { OrderingKey } from '@/models/Filter';
import { StudentExamCorrectionStatusEnum } from '@/models/StudentExam';
import {
  DateRangeFilterParams,
  parseDateRangeParams,
} from '@/utils/filters/dateRangeParams';
import { createOrderingGuard } from '@/utils/filters/ordering';
import { getNumberParam } from '@/utils/filters/searchParams';

const DEFAULT_ORDERING: StudentExamOrdering = '-finished_at';

const isStudentExamOrdering = createOrderingGuard(STUDENT_EXAM_ORDERING_FIELDS);

export type ExamPendenciesMainFilters = {
  search?: string;
  klassId?: number;
  examId?: number;
} & DateRangeFilterParams;

export type ExamPendenciesAdvancedFilters = PendencyAdvancedFilters & {
  course?: number;
};

type ExamPendenciesParams = {
  ordering: StudentExamOrdering;
  page: number;
} & ExamPendenciesMainFilters &
  ExamPendenciesAdvancedFilters;

type UseExamPendenciesFilterProps = {
  defaultKlassId?: number;
};

export default function useExamPendenciesFilter({
  defaultKlassId,
}: UseExamPendenciesFilterProps = {}) {
  const { params, updateParams } = useDynamicParams<ExamPendenciesParams>(
    defaultKlassId ? { klassId: String(defaultKlassId) } : undefined,
  );

  const orderingParam = params.get('ordering');
  const ordering = isStudentExamOrdering(orderingParam)
    ? orderingParam
    : DEFAULT_ORDERING;

  const {
    advancedFilters: pendencyAdvancedFilters,
    advancedFiltersCount: pendencyAdvancedFiltersCount,
    teacherFilter,
    unitsFilter,
  } = usePendencyAdvancedFilters();

  const course = getNumberParam(params, 'course');

  const advancedFilters: ExamPendenciesAdvancedFilters = {
    ...pendencyAdvancedFilters,
    course,
  };

  const advancedFiltersCount = pendencyAdvancedFiltersCount + (course ? 1 : 0);

  const mainFilters: ExamPendenciesMainFilters = {
    search: params.get('search') || undefined,
    klassId: getNumberParam(params, 'klassId'),
    examId: getNumberParam(params, 'examId'),
    ...parseDateRangeParams(params),
  };

  const filters: StudentExamFilterParams = {
    status: [StudentExamCorrectionStatusEnum.PENDING],
    ordering,
    search: mainFilters.search,
    klassId: mainFilters.klassId ? [mainFilters.klassId] : undefined,
    examId: mainFilters.examId ? [mainFilters.examId] : undefined,
    finishedAtAfter: mainFilters.dateRangeAfter,
    finishedAtBefore: mainFilters.dateRangeBefore,
    teacherId: teacherFilter ? [teacherFilter] : undefined,
    unitId: unitsFilter,
    courseId: course ? [course] : undefined,
  };

  const toggleOrdering = (field: OrderingKey<StudentExamOrdering>) =>
    updateParams({
      ordering: ordering === field ? `-${field}` : field,
      page: FIRST_PAGE,
    });

  const updateMainFilters = (updated: Partial<ExamPendenciesMainFilters>) =>
    updateParams({ ...updated, page: FIRST_PAGE });

  const updateAdvancedFilters = (
    updated: Partial<ExamPendenciesAdvancedFilters>,
  ) => updateParams({ ...updated, page: FIRST_PAGE });

  const clearAdvancedFilters = () =>
    updateParams({
      teacher: undefined,
      unit: undefined,
      course: undefined,
      page: FIRST_PAGE,
    });

  return {
    filters,
    mainFilters,
    advancedFilters,
    advancedFiltersCount,
    toggleOrdering,
    updateMainFilters,
    updateAdvancedFilters,
    clearAdvancedFilters,
  };
}
